import React from 'react'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import {
  AnimatedSection,
  fadeInUp,
  fadeInLeft,
  scaleIn,
} from './animations/AnimatedSection'
import { NewBadge } from './common/stand_alone/newBadge'
import { EXTERNAL_LINK } from '../assets/data/external_link'
import { trackDashboardClick } from '../utils/analytics'

const ReadyToJoinSection = () => {
  const navigate = useNavigate()
  return (
    <Section>
      <ContentContainer>
        <AnimatedSection variants={scaleIn}>
          <Logo
            src="/images/logos/full-logo.webp"
            alt="Community of Guardians Logo"
          />
        </AnimatedSection>

        <TextStack>
          <AnimatedSection variants={fadeInLeft} delay={0.1}>
            <Title>
              Ready to Become a <HighlightSpan>Guardian?</HighlightSpan>
            </Title>
          </AnimatedSection>
          <AnimatedSection variants={fadeInLeft} delay={0.2}>
            <Subtitle>
              Whether you&apos;re a researcher seeking impact, an educator
              inspiring the next generation, or a young person ready to make a
              difference — your journey starts here.
            </Subtitle>
          </AnimatedSection>
        </TextStack>

        <AnimatedSection variants={fadeInUp} delay={0.35}>
          <CTAGroup>
            <CTAButton onClick={() => navigate('/quiz')}>
              Take the SDG Quiz
            </CTAButton>
            <SecondaryCTA
              href={EXTERNAL_LINK.dashboard}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackDashboardClick('ready_to_join')}
            >
              Join the Community
              <NewBadge />
            </SecondaryCTA>
          </CTAGroup>
        </AnimatedSection>
      </ContentContainer>
    </Section>
  )
}

const Section = styled.section`
  width: 100%;
  height: 812px;
  background: url('/images/homepage-new/globe.webp') no-repeat center center;
  background-size: cover;
  display: flex;
  align-items: center;
  padding: 200px max(24px, calc((100vw - 1200px) / 2));
  box-sizing: border-box;

  @media (max-width: 1024px) {
    padding: 80px 24px;
    height: auto;
    min-height: 600px;
  }
`

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 32px;
  max-width: 640px;
  width: 100%;
`

const Logo = styled.img.attrs({ loading: 'lazy' })`
  width: 230px;
  height: auto;
  object-fit: contain;
`

const TextStack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;
`

const Title = styled.h2`
  font-family: 'Poppins', sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 48px;
  line-height: 56px;
  letter-spacing: -0.04em;
  color: #000000;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 36px;
    line-height: 44px;
  }
`

const HighlightSpan = styled.span`
  background: linear-gradient(90deg, #0e6409 0%, #bbe148 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  color: transparent;
`

const Subtitle = styled.p`
  font-family: 'Inter', sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: #5e5e5e;
  margin: 0;
  max-width: 600px;
`

const CTAGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
`

const SecondaryCTA = styled.a`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 14px 24px;
  width: 250px;
  height: 52px;
  box-sizing: border-box;
  border: 1px solid #2a2a2a;
  border-radius: 100px;
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
  color: #2a2a2a;
  text-decoration: none;
  transition: all 0.2s ease-in-out;

  &:hover {
    background: rgba(0, 0, 0, 0.05);
  }
`

const CTAButton = styled.button`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 14px 24px;
  width: 250px;
  height: 52px;
  background: #0e6409;
  border: 1px solid #2a2a2a;
  border-radius: 100px;
  font-family: 'Poppins', sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
  color: #ffffff;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  letter-spacing: 0px;

  &:hover {
    background: #0a4a06;
    transform: translateY(-2px);
    opacity: 1;
  }
`

export default ReadyToJoinSection
